import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Layouts, MemberSearchField } from 'components'
import { head } from 'helpers'
import * as api from 'api'
import s from '../jobs/adminForm.module.scss'

const EXPERIENCE_OPTIONS = ['Fresher', '0-1 years', '1-2 years', '2-3 years', '3-6 years', '6-10 years', '10+ years']
const MAX_CHIPS = 15

const initialForm = {
  name: '',
  email: '',
  phone: '',
  jobRole: '',
  experience: '',
  state: '',
  city: '',
  functionalArea: '',
  highestQualification: '',
  specialisation: '',
  yearOfPassing: '',
  currentJobTitle: '',
  currentEmployer: '',
  gender: '',
  dob: '',
  fkUserId: '',
  declaration: false,
}

export default function AdminResumeCreate() {
  const [form, setForm] = useState(initialForm)
  const [keySkills, setKeySkills] = useState([])
  const [skillInput, setSkillInput] = useState('')
  const [certifications, setCertifications] = useState([])
  const [certificationInput, setCertificationInput] = useState('')
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [functionalAreas, setFunctionalAreas] = useState([])
  const [qualifications, setQualifications] = useState([])
  const [loading, setLoading] = useState(false)

  const selectedQualification = useMemo(
    () => qualifications.find(q => q.qualification === form.highestQualification),
    [qualifications, form.highestQualification]
  )

  useEffect(() => {
    head({ title: 'Create Candidate Profile | BCN Admin' })
  }, [])

  useEffect(() => {
    api.publicApi.locations.fetchAllStates({ limit: 100, state: '' }).then(res => {
      if (res.code === 200) setStates(res.payload?.states || [])
    }).catch(() => setStates([]))

    api.publicApi.functionalArea.getArea().then(res => {
      if (Array.isArray(res.payload)) setFunctionalAreas(res.payload)
    }).catch(() => setFunctionalAreas([]))

    api.publicApi.qualification.getQualifications().then(res => {
      if (res?.payload && Array.isArray(res.payload)) setQualifications(res.payload)
      else if (res?.code === 'OK' && Array.isArray(res.payload)) setQualifications(res.payload)
    }).catch(() => setQualifications([]))
  }, [])

  const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }))

  const onQualificationChange = (value) => {
    setForm(prev => ({ ...prev, highestQualification: value, specialisation: '' }))
  }

  const onStateChange = useCallback(async (stateName) => {
    setField('state', stateName)
    setField('city', '')
    if (!stateName) {
      setCities([])
      return
    }
    try {
      const stateRes = await api.publicApi.locations.fetchAllStates({ limit: 200, state: stateName })
      if (stateRes.code === 200 && stateRes.payload?.states?.length) {
        const fkStateId = stateRes.payload.states[0]._id
        const cityRes = await api.publicApi.locations.fetchAllCitiesViaState({ limit: 500, city: '', fkStateId })
        if (cityRes.code === 200) setCities(cityRes.payload?.cities || [])
        else setCities([])
      }
    } catch {
      setCities([])
    }
  }, [])

  const addChip = (list, setList, input, setInput) => {
    if (list.length >= MAX_CHIPS) return
    const val = input.trim()
    if (val && !list.includes(val)) {
      setList(prev => [...prev, val])
      setInput('')
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!form.declaration) {
      Alert.warn('Please accept the declaration')
      return
    }
    if (!form.highestQualification) {
      Alert.warn('Please select highest qualification')
      return
    }
    if (!form.specialisation?.trim()) {
      Alert.warn('Please enter or select specialisation / stream')
      return
    }
    if (certifications.length === 0) {
      Alert.warn('Please add at least one certification')
      return
    }
    if (keySkills.length === 0) {
      Alert.warn('Please add at least one key skill')
      return
    }
    const phone = form.phone.replace(/\D/g, '').slice(0, 10)
    if (!/^[0-9]{10}$/.test(phone)) {
      Alert.warn('Phone must be exactly 10 digits')
      return
    }
    setLoading(true)
    try {
      const payload = {
        ...form,
        phone,
        keySkills,
        certifications,
        yearOfPassing: Number(form.yearOfPassing),
        fkUserId: form.fkUserId?.trim() || undefined,
      }
      const res = await api.admin.resume.create(payload)
      if (res.code === 201) {
        Alert.success(res.message || 'Candidate profile created')
        setForm(initialForm)
        setKeySkills([])
        setSkillInput('')
        setCertifications([])
        setCertificationInput('')
      } else {
        Alert.error(res.message || 'Failed to create profile')
      }
    } catch {
      Alert.error('Failed to create candidate profile')
    } finally {
      setLoading(false)
    }
  }

  const renderSpecialisationField = () => {
    if (!form.highestQualification || !selectedQualification) return null

    if (selectedQualification.type === 'dropdown' && selectedQualification.options?.length > 0) {
      return (
        <div className={s.field}>
          <label>Stream *</label>
          <select
            value={form.specialisation}
            onChange={e => setField('specialisation', e.target.value)}
            required
          >
            <option value="">Select Stream</option>
            {selectedQualification.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      )
    }

    return (
      <div className={s.field}>
        <label>Specialisation *</label>
        <input
          value={form.specialisation}
          onChange={e => setField('specialisation', e.target.value)}
          placeholder={selectedQualification.placeholder || 'Enter Specialisation'}
          required
        />
      </div>
    )
  }

  return (
    <div className={s.page}>
      <Layouts.Classic title="Create Candidate Profile">
        <Link className={s.secondaryBtn} to="/admin/approvalRequests">
          <span className="material-icons-outlined">pending_actions</span>
          Pending Profiles
        </Link>
      </Layouts.Classic>

      <div className={s.hero}>
        <p className={s.eyebrow}>BCN Careers Admin</p>
        <p className={s.heroText}>
          Add a job seeker profile on behalf of a member. Admin-created profiles are published immediately.
        </p>
      </div>

      <form className={s.formCard} onSubmit={handleSubmit}>
        <h2 className={s.sectionTitle}>Personal Details</h2>
        <div className={s.grid}>
          <div className={s.field}>
            <label>Full Name *</label>
            <input value={form.name} onChange={e => setField('name', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Email *</label>
            <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Phone *</label>
            <input
              value={form.phone}
              onChange={e => setField('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="10-digit mobile"
              inputMode="numeric"
              required
            />
          </div>
          <div className={s.field}>
            <label>Gender *</label>
            <select value={form.gender} onChange={e => setField('gender', e.target.value)} required>
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className={s.field}>
            <label>Date of Birth *</label>
            <input type="date" value={form.dob} onChange={e => setField('dob', e.target.value)} required />
          </div>
        </div>

        <h2 className={s.sectionTitle}>Career Details</h2>
        <div className={s.grid}>
          <div className={s.field}>
            <label>Desired Job Role *</label>
            <input value={form.jobRole} onChange={e => setField('jobRole', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Experience *</label>
            <select value={form.experience} onChange={e => setField('experience', e.target.value)} required>
              <option value="">Select Experience</option>
              {EXPERIENCE_OPTIONS.map(exp => <option key={exp} value={exp}>{exp}</option>)}
            </select>
          </div>
          <div className={s.field}>
            <label>Functional Area *</label>
            <select value={form.functionalArea} onChange={e => setField('functionalArea', e.target.value)} required>
              <option value="">Select Functional Area</option>
              {functionalAreas.map(area => (
                <option key={area._id || area.name} value={area.name}>
                  {area.name}
                </option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label>Highest Qualification *</label>
            <select
              value={form.highestQualification}
              onChange={e => onQualificationChange(e.target.value)}
              required
            >
              <option value="">Select Qualification</option>
              {qualifications.map(q => (
                <option key={q._id} value={q.qualification}>{q.qualification}</option>
              ))}
            </select>
          </div>
          {renderSpecialisationField()}
          <div className={s.field}>
            <label>Year of Passing *</label>
            <input
              type="number"
              min="1950"
              max="2100"
              value={form.yearOfPassing}
              onChange={e => setField('yearOfPassing', e.target.value)}
              required
            />
          </div>
          <div className={s.field}>
            <label>Current Job Title *</label>
            <input value={form.currentJobTitle} onChange={e => setField('currentJobTitle', e.target.value)} required />
          </div>
          <div className={s.field}>
            <label>Current Company *</label>
            <input value={form.currentEmployer} onChange={e => setField('currentEmployer', e.target.value)} required />
          </div>
          <div className={`${s.field} ${s.fullWidth}`}>
            <label>Certifications * (max {MAX_CHIPS})</label>
            <div className={s.skillInputWrap}>
              <input
                value={certificationInput}
                onChange={e => setCertificationInput(e.target.value)}
                placeholder="Add a certification and press Add"
                disabled={certifications.length >= MAX_CHIPS}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addChip(certifications, setCertifications, certificationInput, setCertificationInput)
                  }
                }}
              />
              <button
                type="button"
                className={s.addBtn}
                onClick={() => addChip(certifications, setCertifications, certificationInput, setCertificationInput)}
                disabled={certifications.length >= MAX_CHIPS}
              >
                Add
              </button>
            </div>
            {certifications.length > 0 && (
              <div className={s.chips}>
                {certifications.map(cert => (
                  <span key={cert} className={s.chip}>
                    {cert}
                    <button type="button" onClick={() => setCertifications(prev => prev.filter(x => x !== cert))}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className={`${s.field} ${s.fullWidth}`}>
            <label>Key Skills * (max {MAX_CHIPS})</label>
            <div className={s.skillInputWrap}>
              <input
                value={skillInput}
                onChange={e => setSkillInput(e.target.value)}
                placeholder="Add a skill and press Add"
                disabled={keySkills.length >= MAX_CHIPS}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addChip(keySkills, setKeySkills, skillInput, setSkillInput)
                  }
                }}
              />
              <button
                type="button"
                className={s.addBtn}
                onClick={() => addChip(keySkills, setKeySkills, skillInput, setSkillInput)}
                disabled={keySkills.length >= MAX_CHIPS}
              >
                Add
              </button>
            </div>
            {keySkills.length > 0 && (
              <div className={s.chips}>
                {keySkills.map(skill => (
                  <span key={skill} className={s.chip}>
                    {skill}
                    <button type="button" onClick={() => setKeySkills(prev => prev.filter(x => x !== skill))}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <h2 className={s.sectionTitle}>Location</h2>
        <div className={s.grid}>
          <div className={s.field}>
            <label>State *</label>
            <select value={form.state} onChange={e => onStateChange(e.target.value)} required>
              <option value="">Select state</option>
              {states.slice().sort((a, b) => a.state.localeCompare(b.state)).map(st => (
                <option key={st._id} value={st.state}>{st.state}</option>
              ))}
            </select>
          </div>
          <div className={s.field}>
            <label>City *</label>
            <select value={form.city} onChange={e => setField('city', e.target.value)} disabled={!form.state} required>
              <option value="">{form.state ? 'Select city' : 'Select state first'}</option>
              {cities.slice().sort((a, b) => a.city.localeCompare(b.city)).map(ct => (
                <option key={ct._id} value={ct.city}>{ct.city}</option>
              ))}
            </select>
          </div>
          <div className={`${s.field} ${s.fullWidth}`}>
            <MemberSearchField
              value={form.fkUserId}
              onChange={id => setField('fkUserId', id)}
            />
          </div>
        </div>

        <label className={s.checkboxRow}>
          <input type="checkbox" checked={form.declaration} onChange={e => setField('declaration', e.target.checked)} />
          <span>I confirm this candidate profile information is accurate and authorized for publication on BCN.</span>
        </label>

        <p className={s.note}>This profile will be published immediately as Approved and visible in the candidate directory.</p>

        <div className={s.actions}>
          <button type="submit" className={s.submitBtn} disabled={loading}>
            {loading ? 'Creating…' : 'Create & Publish Profile'}
          </button>
        </div>
      </form>
    </div>
  )
}
